import React, { useState, useEffect } from 'react';

interface Product {
  id: string;
  title: string;
  image: string;
  description: string;
}

const ProductsServices: React.FC = () => {
  const [modalData, setModalData] = useState<{ title: string; description: string; image: string } | null>(null);

  // Exact products from HTML - same images, same descriptions
  const products: Product[] = [
    {
      id: 'batteries',
      title: 'BATTERIES',
      image: '/SCRAP BATTERY PURCHASE.jpeg',
      description: 'Industrial & inverter batteries, all capacities. We supply and maintain high-cycle batteries for UPS, inverters, and backup power.',
    },
    {
      id: 'mhe',
      title: 'MHE & HPT',
      image: '/MHE & HPT.jpeg',
      description: 'Complete range of material handling equipment: hand pallet trucks, electric stackers, reach trucks, and hydraulic HPT units.',
    },
    {
      id: 'dock',
      title: 'DOCK LEVELERS',
      image: '/dock.jpeg',
      description: 'Edge-of-dock and pit-mounted levelers, truck restraints, dock shelters – improve logistics efficiency.',
    },
    {
      id: 'racking',
      title: 'RACKING SYSTEM',
      image: '/img31.jpeg',
      description: 'Heavy-duty selective racks, drive-in, cantilever for long goods, and custom mezzanine floors.',
    },
    {
      id: 'inverter',
      title: 'FORKLIFTS BATTERY',
      image: '/INVERTER.jpeg',
      description: 'Industrial grade batteries for forklifts, stackers, reach trucks all capacities and types.',
    },
    {
      id: 'charger',
      title: 'BATTERY CHARGER',
      image: '/BATTERY CHARGER.jpeg',
      description: 'Automatic multi-stage chargers, suitable for forklift batteries, inverter batteries.',
    },
    {
      id: 'scrap',
      title: 'SCRAP BATTERY',
      image: '/bettry.jpeg',
      description: 'Best price for your scrap industrial batteries. We handle collection and eco-friendly recycling.',
    },
    {
      id: 'services',
      title: 'SAJ SERVICES',
      image: '/img33.jpeg',
      description: 'Annual maintenance contracts, 24/7 breakdown assistance, on-site repair, and spares supply.',
    },
  ];

  // Scroll reveal effect
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    
    const checkReveal = () => {
      reveals.forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight - 120) {
          el.classList.add('active');
        }
      });
    };

    window.addEventListener('scroll', checkReveal);
    window.addEventListener('load', checkReveal);
    
    return () => {
      window.removeEventListener('scroll', checkReveal);
      window.removeEventListener('load', checkReveal);
    };
  }, []);

  // Modal functions
  const openModal = (title: string, desc: string, imgUrl: string) => {
    setModalData({ title, description: desc, image: imgUrl });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  return (
    <>
      {/* PRODUCTS section */}
      <section id="products" className="reveal max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="section-header text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 inline-block relative pb-3">
            Products & Services
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-teal-500"></span>
          </h2>
        </div>

        {/* Card grid */}
        <div className="card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {products.map((product) => (
            <div key={product.id} className="service-card bg-white bg-opacity-90 backdrop-blur-sm rounded-3xl border border-amber-500 border-opacity-20 hover:border-amber-500 transition-all duration-300 shadow-md hover:shadow-xl overflow-hidden flex flex-col group">
              <div className="card-img h-48 overflow-hidden">
                <img src={product.image} alt={product.title} className="w-full h-full object-cover transition-transform duration-600 group-hover:scale-110" />
              </div>
              <div className="card-content p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{product.title}</h3>
                <button 
                  onClick={() => openModal(product.title, product.description, product.image)}
                  className="read-more-link text-teal-500 font-semibold inline-flex items-center gap-2 hover:text-teal-600 transition-colors mt-auto bg-transparent border-none cursor-pointer text-base"
                >
                  <i className="fas fa-plus-circle"></i> Read more
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MODAL */}
      {modalData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
        >
          <div className="bg-white rounded-3xl p-8 max-w-md w-full relative shadow-2xl">
            {/* Close Button */}
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-3xl text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>

            {/* Product Image */}
            <div className="mb-6">
              <img 
                src={modalData.image} 
                alt={modalData.title}
                className="w-full h-48 object-cover rounded-xl"
              />
            </div>

            {/* Product Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{modalData.title}</h3>

            {/* Description */}
            <p className="text-gray-600 mb-6 leading-relaxed">
              {modalData.description}
            </p>

            {/* Enquire Now Button */}
            <button
              onClick={() => {
                closeModal();
                const enquirySection = document.getElementById('enquiry');
                if (enquirySection) {
                  enquirySection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="w-full bg-amber-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-600 transition-colors"
            >
              Enquire now
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsServices;